import type { SVGProps } from "react";
import styles from "./SettingsProtestHistoryPage.module.css";
import { Modal } from "@/shared/modal";
// import { ChevronRightIcon } from "@/assets/svg";
import { useSettingsProtestHistoryViewModel } from "@/features/settings/viewModal/useSettingsProtestHistoryViewModel";
import type { ProtestsExemptionsPayment } from "@/features/settings/services/protestsExemptions.service";

type StatusTheme = "approved" | "filed" | "inReview" | "rejected";
type StepVisualState = "completed" | "active" | "pending";

const stepperNodeClassMap: Record<StepVisualState, string> = {
  completed: "stepperNodeCompleted",
  active: "stepperNodeActive",
  pending: "stepperNodePending",
};

const normalizeStatus = (value?: string | null): string => {
  return (value || "").toLowerCase().replace(/\s+/g, "").replace(/_/g, "");
};

const getStatusTheme = (record: ProtestsExemptionsPayment): StatusTheme => {
  const status = normalizeStatus(record.protest_status || record.homestead_status);

  if (status === "approved") {
    return "approved";
  }
  if (status === "rejected" || status === "reject") {
    return "rejected";
  }
  if (status === "inprogress") {
    return "inReview";
  }
  return "filed";
};

const getStepVisualState = (
  index: number,
  steps: Array<{ done: boolean }>,
  theme: StatusTheme
): StepVisualState => {
  const step = steps[index];
  if (!step) {
    return "pending";
  }
  if (theme === "approved" || theme === "rejected") {
    return step.done ? "completed" : "pending";
  }
  if (theme === "inReview") {
    if (index === 0) {
      return "completed";
    }
    if (index === 1) {
      return "active";
    }
    return "pending";
  }
  if (index === 0 && step.done) {
    return "completed";
  }
  if (index === 1) {
    return "active";
  }
  return "pending";
};

const HouseIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path
      d="M4 10.5 12 4l8 6.5V19a1 1 0 0 1-1 1h-5v-6H10v6H5a1 1 0 0 1-1-1v-8.5Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
  </svg>
);

const DocumentIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path
      d="M8 3h6l4 4v14a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <path d="M14 3v4h4" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
  </svg>
);

const CalendarIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <rect x="4" y="5" width="16" height="15" rx="2" stroke="currentColor" strokeWidth="1.8" />
    <path d="M8 3v4M16 3v4M4 10h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const TaxYearIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <rect x="3" y="6" width="18" height="13" rx="2" stroke="currentColor" strokeWidth="1.8" />
    <path
      d="M7 10h4M13 10h4M7 14h10"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const EyeIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path
      d="M2.5 12C4.5 7.5 8 5 12 5s7.5 2.5 9.5 7c-2 4.5-5.5 7-9.5 7s-7.5-2.5-9.5-7Z"
      stroke="currentColor"
      strokeWidth="1.8"
    />
    <circle cx="12" cy="12" r="2.5" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);

const CheckIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path
      d="M6 12.5 10 16.5 18 8.5"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ClockIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.8" />
    <path d="M12 8v4.5l2.5 1.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const XIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path
      d="M6 6l12 12M6 18L18 6"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const StatusBadgeIcon = ({ theme }: { theme: StatusTheme }) => {
  if (theme === "approved") {
    return <CheckIcon className={styles.statusBadgeIcon} aria-hidden="true" />;
  }
  if (theme === "inReview") {
    return <ClockIcon className={styles.statusBadgeIcon} aria-hidden="true" />;
  }
  return <DocumentIcon className={styles.statusBadgeIcon} aria-hidden="true" />;
};

export const SettingsProtestHistoryPage = () => {
  const {
    activeTab,
    setActiveTab,
    page,
    setPage,
    pageSize,
    setPageSize,
    statusFilter,
    setStatusFilter,
    records,
    totalPages,
    totalRecords,
    isLoading,
    isError,
    previewRecord,
    setPreviewRecord,
    activePreviewKey,
    setActivePreviewKey,
    previewDetails,
    currentPreviewUrl,
    helpers,
  } = useSettingsProtestHistoryViewModel();

  return (
    <div className={styles.page}>
      <h1 className={styles.pageTitle}>Protest History</h1>
      <p className={styles.pageDescription}>All protests you&apos;ve filed for your properties</p>

      <div className={styles.tabList} role="tablist" aria-label="Protest history tabs">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "protest"}
          className={`${styles.tab} ${activeTab === "protest" ? styles.tabActive : ""}`.trim()}
          onClick={() => setActiveTab("protest")}
        >
          protest
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "exemption"}
          className={`${styles.tab} ${activeTab === "exemption" ? styles.tabActive : ""}`.trim()}
          onClick={() => setActiveTab("exemption")}
        >
          exemption
        </button>
      </div>

      <div className={styles.filtersRow}>
        <label className={styles.filterLabel}>
          Status
          <select
            className={styles.filterSelect}
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(event.target.value);
              setPage(1);
            }}
          >
            <option value="">All</option>
            <option value="not_filed">Not Filed</option>
            <option value="filed">Submitted</option>
            <option value="inprogress">In Progress</option>
            <option value="approved">Approved</option>
            <option value="reject">Rejected</option>
            {/* <option value="pending">Pending</option> */}
          </select>
        </label>

        <label className={styles.filterLabel}>
          Page Size
          <select
            className={styles.filterSelect}
            value={pageSize}
            onChange={(event) => {
              setPageSize(Number(event.target.value));
              setPage(1);
            }}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
        </label>
      </div>

      <div className={styles.cardList}>
        {isLoading && <article className={styles.historyCard}>Loading records...</article>}
        {!isLoading && isError && (
          <article className={styles.historyCard}>Unable to load records. Please refresh.</article>
        )}
        {!isLoading && !isError && records.length === 0 && (
          <article className={styles.historyCard}>No records found.</article>
        )}
        {records.map((record) => {
          const statusTheme = getStatusTheme(record);
          const timelineSteps = helpers.getTimelineSteps(record);

          return (
            <article className={styles.historyCard} key={record.id} data-status={statusTheme}>
              <div className={styles.cardBody}>
                <div className={styles.cardTopRow}>
                  <div className={styles.propertyIconWrap}>
                    <HouseIcon className={styles.propertyIcon} aria-hidden="true" />
                  </div>

                  <div className={styles.cardMain}>
                    <div className={styles.cardHeader}>
                      <h2 className={styles.propertyAddress}>{helpers.toAddress(record)}</h2>
                      <span className={styles.statusBadge}>
                        <StatusBadgeIcon theme={statusTheme} />
                        {helpers.toTitleCase(
                          record.protest_status || record.homestead_status || "filed"
                        )}
                      </span>
                    </div>

                    <p className={styles.meta}>
                      Property Type:{" "}
                      {helpers.toTitleCase(record.property_details?.property_type || "Residential")}{" "}
                      &#8226; 1 Day Found
                    </p>

                    <div className={styles.detailList}>
                      <p className={styles.detail}>
                        <DocumentIcon className={styles.detailIcon} aria-hidden="true" />
                        <span>Protest ID: {record.id}</span>
                      </p>
                      <p className={styles.detail}>
                        <CalendarIcon className={styles.detailIcon} aria-hidden="true" />
                        <span>Filed on: {helpers.formatDate(record.created_at)}</span>
                      </p>
                      <p className={styles.detail}>
                        <TaxYearIcon className={styles.detailIcon} aria-hidden="true" />
                        <span>Tax Year: {record.year ?? "-"}</span>
                      </p>
                    </div>

                    <div className={styles.stepper}>
                      {timelineSteps.map((step, index, list) => {
                        const visualState = getStepVisualState(index, list, statusTheme);
                        const connectorDone =
                          index > 0 &&
                          getStepVisualState(index - 1, list, statusTheme) === "completed";

                        return (
                          <div key={step.key} className={styles.stepperStep}>
                            <div className={styles.stepperTrack}>
                              {index > 0 && (
                                <div
                                  className={`${styles.stepperLine} ${connectorDone ? styles.stepperLineDone : ""}`.trim()}
                                  aria-hidden="true"
                                />
                              )}
                              <div
                                className={`${styles.stepperNode} ${styles[stepperNodeClassMap[visualState]]}`.trim()}
                                aria-hidden="true"
                              >
                                {visualState === "completed" &&
                                  step.key === "result" &&
                                  statusTheme === "rejected" && (
                                    <XIcon className={styles.stepperCheck} />
                                  )}
                                {visualState === "completed" &&
                                  !(step.key === "result" && statusTheme === "rejected") && (
                                    <CheckIcon className={styles.stepperCheck} />
                                  )}
                              </div>
                              {index < list.length - 1 && (
                                <div
                                  className={`${styles.stepperLine} ${visualState === "completed" ? styles.stepperLineDone : ""}`.trim()}
                                  aria-hidden="true"
                                />
                              )}
                            </div>
                            <span className={styles.stepperLabel}>{step.label}</span>
                            <span className={styles.stepperDate}>{step.date}</span>
                          </div>
                        );
                      })}
                    </div>

                    <div className={styles.cardFooter}>
                      <button
                        type="button"
                        className={styles.viewDetailsButton}
                        onClick={() => {
                          const details = helpers.collectDocumentDetails(record);
                          setPreviewRecord(record);
                          setActivePreviewKey(details[0]?.key ?? null);
                        }}
                      >
                        <EyeIcon className={styles.viewDetailsIcon} aria-hidden="true" />
                        View Details
                      </button>
                      {/* <ChevronRightIcon className={styles.cardChevron} aria-hidden="true" /> */}
                    </div>
                  </div>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      {!isLoading && !isError && totalPages > 0 && (
        <div className={styles.paginationRow}>
          <button
            type="button"
            className={styles.paginationButton}
            disabled={page <= 1}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
          >
            Previous
          </button>
          <span className={styles.paginationInfo}>
            Page {page} of {totalPages} ({totalRecords} records)
          </span>
          <button
            type="button"
            className={styles.paginationButton}
            disabled={page >= totalPages}
            onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
          >
            Next
          </button>
        </div>
      )}

      {previewRecord && (
        <Modal
          isOpen
          onClose={() => {
            setPreviewRecord(null);
            setActivePreviewKey(null);
          }}
          unconstrained
          className={styles.modalCard}
          headerClassName={styles.modalHeader}
          title={`Documents - ${helpers.toAddress(previewRecord)}`}
          titleClassName={styles.modalTitle}
          bodyClassName={styles.modalBody}
          ariaLabel="Document preview"
        >
          <div className={styles.docList}>
            {previewDetails.length === 0 && (
              <p className={styles.noDocs}>No documents found for this record.</p>
            )}
            {previewDetails.map((doc) => (
              <button
                key={doc.key}
                type="button"
                className={`${styles.docItem} ${activePreviewKey === doc.key ? styles.docItemActive : ""}`.trim()}
                onClick={() => setActivePreviewKey(doc.key)}
              >
                <span>{doc.label}</span>
                <a
                  href={doc.url}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(event) => event.stopPropagation()}
                >
                  Open
                </a>
              </button>
            ))}
          </div>

          <div className={styles.previewPane}>
            {!currentPreviewUrl && <p className={styles.noDocs}>Select a document to preview.</p>}
            {currentPreviewUrl && helpers.isImageUrl(currentPreviewUrl) && (
              <img src={currentPreviewUrl} alt="Document preview" className={styles.previewImage} />
            )}
            {currentPreviewUrl && helpers.isPdfUrl(currentPreviewUrl) && (
              <iframe src={currentPreviewUrl} title="PDF preview" className={styles.previewFrame} />
            )}
            {currentPreviewUrl &&
              !helpers.isImageUrl(currentPreviewUrl) &&
              !helpers.isPdfUrl(currentPreviewUrl) && (
                <a
                  href={currentPreviewUrl}
                  target="_blank"
                  rel="noreferrer"
                  className={styles.openExternal}
                >
                  Open document in new tab
                </a>
              )}
          </div>
        </Modal>
      )}
    </div>
  );
};
